import BaseAPI from "./BaseAPI";
import { store, settingsContext } from "@/store";
import { getAPIUrlV2 } from "../apiConfig";
import { clientRoute } from "../routes";

// Token contents may refresh for the same user; selected staff can change
// independently of token-derived getters on a shared device.
const checkFormClientContext = () => JSON.stringify([
    store.getters.domain, store.state.customerID, Boolean(store.getters.token),
    store.getters.tokenUserID?.(), store.state.user?.id || "", store.getters.userID?.()
]);

export default class ClientAPI extends BaseAPI {
    constructor(emitterInstance) {
        super(emitterInstance, clientRoute, "client");
    }

    async Get() {
        return this._request("get", this._url());
    }

    async GetSync() {
        const context = settingsContext();
        const revision = store.state.settingsRevision;
        const result = await this._request("get", this._url(), null, {
            headers: {
                Authorization: `Bearer ${store.state.token}`,
                "Customer-ID": store.getters.domain
            }
        });
        if (store.state.token && settingsContext() === context) {
            store.commit(
                "setClient",
                revision === store.state.settingsRevision
                    ? result
                    : { ...result, settings: store.state.client?.settings || store.state.settings }
            );
        }
        return result;
    }

    async Update(data) {
        const result = await this._request("put", this._url(), data);
        this._emit("update");
        return result;
    }

    async RemoveCheckForm(id) {
        const context = checkFormClientContext();
        const result = await this._request(
            "post",
            `${getAPIUrlV2("client/check-forms/remove")}`,
            { form_id: id },
            {
                headers: {
                    Authorization: `Bearer ${store.state.token}`,
                    "Customer-ID": store.getters.domain
                }
            }
        );
        if (context === checkFormClientContext()) {
            store.commit("setClient", result);
            this._emit("update");
        }
        return result;
    }

    async BatchRemoveCheckForms(ids) {
        for (const id of ids) {
            await this.RemoveCheckForm(id);
        }
        return store.state.client;
    }

    async AddCheckForm(id) {
        const context = checkFormClientContext();
        const result = await this._request(
            "post",
            `${getAPIUrlV2("client/check-forms/add")}`,
            { form_id: id },
            {
                headers: {
                    Authorization: `Bearer ${store.state.token}`,
                    "Customer-ID": store.getters.domain
                }
            }
        );
        if (context === checkFormClientContext()) {
            store.commit("setClient", result);
            this._emit("update");
        }
        return result;
    }

    async ActivateNewRevision(oldID, newID) {
        const result = await this._request(
            "post",
            `${getAPIUrlV2("client/check-forms/activate-revision")}`,
            { old_form_id: oldID, new_form_id: newID },
            {
                headers: {
                    Authorization: `Bearer ${store.state.token}`,
                    "Customer-ID": store.getters.domain
                }
            }
        );
        store.commit("setClient", result);
        this._emit("update");
        return result;
    }

    async GetPayData() {
        return this._request("get", `${getAPIUrlV2("billing")}`);
    }

    async CreateCheckoutSession(idempotencyKey = null) {
        const options = { rawData: true };
        if (idempotencyKey) options.headers = { "Idempotency-Key": idempotencyKey };
        const result = await this._request("post", `${getAPIUrlV2("stripe/create-checkout-session")}`, null, options);
        if (result.status && result.data?.url) {
            return result.data.url;
        }
        throw new Error("Invalid response from checkout session creation");
    }

    async CreatePortalSession(idempotencyKey = null) {
        const options = { rawData: true };
        if (idempotencyKey) options.headers = { "Idempotency-Key": idempotencyKey };
        const result = await this._request("post", `${getAPIUrlV2("stripe/create-portal-session")}`, null, options);
        if (result.status && result.data?.url) {
            return result.data.url;
        }
        throw new Error("Invalid response from portal session creation");
    }

    async GetBillingDetails() {
        const result = await this._request("get", `${getAPIUrlV2("stripe/billing-details")}`, null, { rawData: true });
        if (result.status) {
            return result.data;
        }
        throw new Error("Invalid response from billing details");
    }

    // Resolves the assigned plan's price for display before a subscription
    // exists. Returns null when the price can't be resolved (no plan assigned,
    // lookup failed) so the caller can simply hide the price line.
    async GetPlanPrice() {
        const result = await this._request("get", `${getAPIUrlV2("stripe/plan-price")}`, null, { rawData: true });
        if (result.status) {
            return result.data;
        }
        return null;
    }
}
